import { createContext, useEffect, useRef, useState } from "react";

export const BarCodeScannerContext = createContext({
    scannedString: "",
    setScannedString: () => {},
    resetScannedString: () => {},
    setEnableScan: () => {},
    enableScan: true,
    setEnableChainedScan: () => {},
    withScanDisabled: async (fn) => fn(),
});

export function BarCodeScannerProvider({ children }) {
    const [scannedString, setScannedString] = useState("");

    const [enableScanState, setEnableScanState] = useState(true);
    const [enableChainedScanState, setEnableChainedScanState] = useState(false)
    const enableScanRef = useRef(true);

    const [focusState, setFocusState] = useState(false);
    const focusRef = useRef(false);

    const scannedCodeRef = useRef("");
    const scanTimeoutRef = useRef(null);
    const scanStartTimeRef = useRef(null); // First character time code
    const enableChainedScan = useRef(false);
    const chainedScanList = useRef([]);

    // Minimal Barcode length
    const MIN_BARCODE_LENGTH = 4;

    const setEnableScan = (valueOrUpdater) => {
        const next =
            typeof valueOrUpdater === "function"
                ? valueOrUpdater(enableScanRef.current)
                : valueOrUpdater;
        enableScanRef.current = !!next;
        setEnableScanState(!!next);
    };

    const setEnableChainedScan = (valueOrUpdater) => {
        const next =
            typeof valueOrUpdater === "function"
                ? valueOrUpdater(enableChainedScan.current)
                : valueOrUpdater;
        enableChainedScan.current = !!next;
        setEnableChainedScanState(!!next);
    }

    const withScanDisabled = async (fn) => {
        const prev = enableScanRef.current;
        try {
            setEnableScan(false);
            return await fn();
        } finally {
            setEnableScan(prev);
        }
    };

    const isEditableElement = (el) => {
        if (!el || el === document.body) return false;
        const tag = el.tagName;
        if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true;
        if (el.isContentEditable) return true;
        const role = el.getAttribute && el.getAttribute("role");
        if (role === "textbox" || role === "combobox" || role === "searchbox") return true;
        // Testing descending input because some MUI inputs have an internal input.
        if (el.querySelector && (el.querySelector("input[contenteditable='true'], textarea[contenteditable='true']"))) return true;
        return false;
    };

    useEffect(() => {
        const updateFocusFromActive = () => {
            const active = document.activeElement;
            const hasFocus = isEditableElement(active);
            focusRef.current = hasFocus;
            setFocusState(hasFocus);
        };

        const onFocus = (e) => {
            if (e.target.tagName === "INPUT") {
                focusRef.current = true;
                setFocusState(true);
            }
        };
        const onBlur = (e) => {
            if (e.target.tagName === "INPUT") {
                focusRef.current = false;
                setFocusState(false);
            }
        };

        const keydownHandler = (event) => {
            // Clearing timeout
            if (scanTimeoutRef.current) {
                clearTimeout(scanTimeoutRef.current);
            }

            // Using ref for an asynchronous behavior
            if (!focusRef.current && (enableScanRef.current  || enableChainedScan.current)) {
                if (event.key === "Enter" && scannedCodeRef.current.length >= MIN_BARCODE_LENGTH) {
                    event.preventDefault();

                    const elapsed = scanStartTimeRef.current ? Date.now() - scanStartTimeRef.current : Infinity;
                    const value = scannedCodeRef.current;
                    scannedCodeRef.current = "";
                    scanStartTimeRef.current = null;

                    if (scanTimeoutRef.current) {
                        clearTimeout(scanTimeoutRef.current);
                        scanTimeoutRef.current = null;
                    }

                    if (value.length >= MIN_BARCODE_LENGTH && elapsed < 500) {
                        setScannedString("");
                        if (enableChainedScan.current) {
                            setTimeout(() => {
                                chainedScanList.current.push(value)
                            }, 0);

                            return;
                        }
                        setTimeout(() => setScannedString(value), 0);
                    }

                    return;
                } else {
                    // Special keys (Shift, Meta, F1-F12, Arrow*, etc.) have length > 1.
                    let key = event.key.length === 1 ? event.key : "";
                    if (key === "§") key = "-";

                    if (key === "Unidentified") {
                        if (event.keyCode >= 32 && event.keyCode <= 126) {
                            key = String.fromCharCode(event.keyCode);
                        } else {
                            key = "";
                        }
                    }

                    if (key !== "") {
                        // To check the elapsed time between the first and the last key.
                        if (scannedCodeRef.current === "") {
                            scanStartTimeRef.current = Date.now();
                        }
                        scannedCodeRef.current += key;
                    }
                }
            }

            // For hardware who don't send "Enter" key.
            scanTimeoutRef.current = setTimeout(() => {
                const code = scannedCodeRef.current;
                const elapsed = scanStartTimeRef.current ? Date.now() - scanStartTimeRef.current : Infinity;
                scannedCodeRef.current = "";
                scanStartTimeRef.current = null;

                if (code.length >= MIN_BARCODE_LENGTH && elapsed < 500) {
                    setScannedString("");
                    setTimeout(() => setScannedString(code), 0);
                }
            }, 200);
        };

        // document.addEventListener("focus", onFocus, true);
        // document.addEventListener("blur", onBlur, true);
        document.addEventListener('focusin', updateFocusFromActive)
        document.addEventListener('focusout', () => setTimeout(updateFocusFromActive, 0))
        document.addEventListener("keydown", keydownHandler);

        return () => {
            document.removeEventListener("focus", onFocus, true);
            document.removeEventListener("blur", onBlur, true);
            document.removeEventListener("keydown", keydownHandler);
        };
    }, []);

    useEffect(() => {
        if (!enableChainedScan.current || !enableScanRef.current) return;

        const interval = setInterval(() => {
            if (!enableScanRef.current) return;

            if (chainedScanList.current.length > 0) {
                const value = chainedScanList.current.shift();
                setScannedString("");
                setTimeout(() => setScannedString(value), 0.1);
            }
        }, 200);

        return () => clearInterval(interval);
    }, [enableChainedScanState, enableScanState]);

    function resetScannedString() {
        setScannedString("");
        chainedScanList.current = []
    }

    return (
        <BarCodeScannerContext.Provider
            value={{
                scannedString,
                setScannedString,
                resetScannedString,
                setEnableScan, // synchrone pour le handler via ref
                enableScan: enableScanState,
                withScanDisabled,
                setEnableChainedScan
            }}
        >
            {children}
        </BarCodeScannerContext.Provider>
    );
}
